import {
  Component,
  computed,
  inject,
  input,
  signal,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SearchFieldComponent } from '../../../../shared/components/search-field/search-field.component';
import { MailboxService } from '../../../../core/services/mailbox.service';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import { TableComponent } from '../../../../shared/components/table/table.component';
import { PageEvent } from '@angular/material/paginator';
import { WebsiteMailboxStatus, User } from '../../../../shared/models/mailbox.models';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';
import { Status } from '../../../../shared/models/status-label.model';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  filter,
  map,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { ConfirmationDialogComponent } from '../../../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { SnackbarComponent } from '../../../../shared/components/snackbar/snackbar.component';
import { AddMailboxDialogComponent } from '../../dialogs/add-mailbox-dialog/add-mailbox-dialog.component';
import { BytesToSizePipe } from '../../../../shared/pipes/bytes-to-size-pipe';
import { WebsocketService } from '../../../../core/services/websocket.service';
import { MatRipple } from '@angular/material/core';

@Component({
  selector: 'app-mailbox',
  imports: [
    MatCardModule,
    FontAwesomeModule,
    MatSlideToggleModule,
    SearchFieldComponent,
    TableComponent,
    StatusLabelComponent,
    MatTableModule,
    MatTooltipModule,
    AsyncPipe,
    MatButtonModule,
    MatRipple,
  ],
  providers: [BytesToSizePipe],
  templateUrl: './mailbox.component.html',
  styleUrl: './mailbox.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailboxComponent implements OnInit, OnDestroy {
  private mailboxService = inject(MailboxService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  private sizePipe = inject(BytesToSizePipe);

  private websocketService = inject(WebsocketService);

  websiteData = input.required<WebsiteData>();

  website = computed(() => this.websiteData().website);

  website$ = toObservable(this.website);

  refresh$ = new BehaviorSubject<void>(undefined);

  noService = signal(false);

  creatingService = signal(false);

  mailbox$ = this.refresh$.pipe(
    switchMap(() => this.website$),
    switchMap((w) =>
      this.mailboxService.getWebsiteMailbox(w.id).pipe(
        map(({ mailbox }) => mailbox),
        tap((mailbox) => {
          this.serviceEnabled.set(
            mailbox.status === WebsiteMailboxStatus.ACTIVE ||
              mailbox.status === WebsiteMailboxStatus.CREATED,
          );
        }),
        catchError((error) => {
          if (error.status === 404) {
            this.noService.set(true);
            return EMPTY;
          }

          return throwError(() => error);
        }),
      ),
    ),
    shareReplay(1),
  );

  filters = signal<{ page: number; pageSize: number; searchText?: string }>({
    page: 1,
    pageSize: 10,
  });

  filters$ = toObservable(this.filters);

  mailboxUsers$ = combineLatest([this.mailbox$, this.filters$]).pipe(
    map(([mailbox, filters]) => {
      const records = mailbox?.users ?? [];

      // Search
      const filteredRecords = filters.searchText
        ? records.filter((record) =>
            record.address.toLowerCase().includes(filters.searchText!.toLowerCase()),
          )
        : records;

      // Pagination
      const start = (filters.page - 1) * filters.pageSize;
      const end = start + filters.pageSize;

      return filteredRecords.slice(start, end);
    }),
  );

  destroyed$ = new Subject();

  serviceEnabled = signal(false);

  loadingService = signal(false);

  mailboxesColumns = [
    {
      columnDef: 'mailbox',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'email',
      header: 'Email',
      cell: (element: User) => `${element.address}`,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: () => '',
      useTemplate: true,
    },
    {
      columnDef: 'quota',
      header: 'Used Quota',
      cell: (element: User) =>
        element.blockUsed !== -1
          ? `${this.sizePipe.transform(element.blockUsed)} / ${this.sizePipe.transform(element.blockHard)}`
          : 'N / A',
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  ngOnInit(): void {
    this.websocketService
      .listen<{ body: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ body }) =>
            body === `Mailbox for website with id: ${this.website().id} created successfully.` ||
            body === `Mailbox creation for website with id: ${this.website().id} failed...`,
        ),
      )
      .subscribe(() => {
        this.creatingService.set(false);
        this.noService.set(false);
        this.refresh$.next();
      });

    this.websocketService
      .listen<{ body: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ body }) =>
            body === `Mailbox for website with id: ${this.website().id} deleted successfully.`,
        ),
      )
      .subscribe(() => {
        this.noService.set(true);
        this.loadingService.set(false);
        this.refresh$.next();
      });

    this.websocketService
      .listen<{ body: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ body }) =>
            body ===
              `Mailbox User for website with id: ${this.website().id} created successfully.` ||
            body ===
              `Mailbox User for website with id: ${this.website().id} has been updated successfully.` ||
            body === `Mailbox user for website with id: ${this.website().id} deleted successfully.`,
        ),
      )
      .subscribe(() => {
        this.refresh$.next();
      });

    this.websocketService
      .listen<{ body: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        withLatestFrom(this.mailbox$),
        filter(
          ([{ body }, mailbox]) =>
            body === `Mailbox with id: ${mailbox.id}, enabled successfully` ||
            body === `Mailbox with id: ${mailbox.id}, disabled successfully`,
        ),
      )
      .subscribe(() => {
        this.refresh$.next();
        this.loadingService.set(false);
      });
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, searchText: text }));
  }

  handlePageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1, pageSize: event.pageSize }));
  }

  goToWebMail(): void {
    const server = this.websiteData().server;
    if (server) window.open(`https://webmail.${server.hostname}`, '_blank');
  }

  onServiceToggleChange(event: MatSlideToggleChange): void {
    event.source.toggle(); // To prevent visual toggle
    if (this.serviceEnabled()) this.deleteService();
    else this.createService();
  }

  onUserToggleChange(event: MatSlideToggleChange, id: number): void {
    event.source.toggle(); // To prevent visual toggle
    this.mailboxService.toggleMailboxUser(id, event.checked).subscribe();
  }

  addMailboxService(): void {
    const systemUserId = this.websiteData().systemUser?.id;
    const websiteId = this.website().id;
    const server = this.websiteData().server?.id;
    if (systemUserId && server)
      this.mailboxService
        .createMailbox({
          systemUserId,
          websiteId,
          server,
        })
        .subscribe({
          next: () => {
            this.snackbar.openFromComponent(SnackbarComponent, {
              data: {
                text: `Mailbox service creation started!`,
              },
            });
            this.creatingService.set(true);
          },
          error: (error) => {
            this.statusModalService.open({
              state: 'error',
              message: error.message,
            });
          },
        });
  }

  deleteMailboxService(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Mail Service',
          paragraphs: [
            `This action will permanently delete the Mail Service of ${this.website().domain} and disable all mailboxes.`,
            `Are you sure you want to delete the Mail Service of ${this.website().domain}?`,
          ],
          primaryButtonText: 'Delete Mail Service',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.mailbox$.pipe(take(1))),
        switchMap((data) => this.mailboxService.deleteMailbox(data.id)),
        tap(() => this.loadingService.set(true)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mailbox Service deletion started!`,
            },
          });
          this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  addMailbox(): void {
    this.mailbox$
      .pipe(
        take(1),
        switchMap((m) =>
          this.dialog
            .open(AddMailboxDialogComponent, {
              data: {
                subTitle: this.website().domain,
                mailboxId: m.id,
                systemUserId: this.websiteData().systemUser?.id,
                domain: this.website().domain,
              },
            })
            .afterClosed(),
        ),
        filter(Boolean),
        switchMap((data) => this.mailboxService.createMailboxUser(data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mailbox adding started!`,
            },
          });
          this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  deleteMailbox(user: User): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Mailbox',
          paragraphs: [
            `This action will permanently delete mailbox ${user.address} and all of its contents. If a backup is available, the mailbox can be restored.`,
            `Are you sure you want to delete mailbox ${user.address}?`,
          ],
          primaryButtonText: 'Delete Mailbox',
          type: 'delete',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.mailboxService.deleteMailboxUser(user.id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mailbox deletion started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  editMailbox(mailbox: User): void {
    this.dialog
      .open(AddMailboxDialogComponent, {
        data: {
          title: 'Mailbox settings',
          subTitle: this.website().domain,
          primaryButtonText: 'Save Changes',
          user: mailbox,
          domain: this.website().domain,
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data) => this.mailboxService.updateMailboxUser(mailbox.id, data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mailbox added!`,
            },
          });
          this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  createService(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Enable Mail Service',
          paragraphs: [
            `Enabling the WPX mail service for ${this.website().domain} will configure the required DNS records and spam filtering for WPX Mail.`,
            'If the domain is currently using another email provider, its mail service may stop working after this change.',
            `Are you sure you want to enable the WPX mail service for ${this.website().domain}?`,
          ],
          primaryButtonText: 'Enable Mail Service',
          icon: ['fas', 'play'],
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.mailbox$.pipe(take(1))),
        switchMap((m) => this.mailboxService.toggleMailbox(m.id)),
        tap(() => this.loadingService.set(true)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mail service update started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  deleteService(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Disable Mail Service',
          paragraphs: [
            `Disabling the WPX mail service for ${this.website().domain} will stop WPX from handling email for this domain.`,
            'If the client will be using another email provider, make sure its DNS records are already configured to avoid mail delivery issues.',
            `Are you sure you want to disable the WPX mail service for ${this.website().domain}?`,
          ],
          primaryButtonText: 'Disable Mail Service',
          icon: ['fas', 'pause'],
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.mailbox$.pipe(take(1))),
        switchMap((m) => this.mailboxService.toggleMailbox(m.id)),
        tap(() => this.loadingService.set(true)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Mail service update started!`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.message,
          });
        },
      });
  }

  toStatus(key: WebsiteMailboxStatus): Status {
    switch (key) {
      case WebsiteMailboxStatus.ACTIVE:
      case WebsiteMailboxStatus.CREATED:
        return Status.ACTIVE;
      case WebsiteMailboxStatus.CREATING:
        return Status.IN_PROGRESS;
      case WebsiteMailboxStatus.UPDATING:
        return Status.PENDING;
      case WebsiteMailboxStatus.DISABLED:
        return Status.DISABLED;
      default:
        return Status.ERROR;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
